import clsx from "clsx";

import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import { Button } from "../base/Button";
import { TextField } from "../base/TextField";
import { ITitleConfigCategorySummary } from "../../models/Category/TitleConfig";

interface CategoryListProps {
  categories: ITitleConfigCategorySummary[];
  selectedId: string | undefined;
  searchTerm: string;
  isLoading: boolean;
  error: Error | null;
  onSearchChange: (value: string) => void;
  onSelect: (partCategoryId: string) => void;
  onAdd: () => void;
  onRemove: (category: ITitleConfigCategorySummary) => void;
  onRebuildAll: () => void;
  isRebuilding: boolean;
}

export default function CategoryList({
  categories,
  selectedId,
  searchTerm,
  isLoading,
  error,
  onSearchChange,
  onSelect,
  onAdd,
  onRemove,
  onRebuildAll,
  isRebuilding,
}: CategoryListProps) {
  const isSearching = searchTerm.trim().length > 0;

  return (
    <div className="bg-white rounded shadow-sm border border-gray-200 flex flex-col max-h-[calc(100vh-12rem)]">
      <div className="p-3 border-b border-gray-100">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
            Configured Categories
          </h2>
          <div className="flex items-center gap-1">
            {/* Prompts are compiled on save, so a change to the prompt rules only reaches a
                category when it is next saved. This rebuilds every saved one in one go. */}
            <span title="Rebuild every saved prompt with the current rules. Customers get the changes on the next category sync.">
              <Button
                size="xs"
                variant="outlined"
                onClick={onRebuildAll}
                disabled={isRebuilding || categories.length === 0}
              >
                <span className="flex items-center gap-1">
                  <ArrowPathIcon
                    className={clsx("h-3.5 w-3.5", isRebuilding && "animate-spin")}
                  />
                  {isRebuilding ? "Rebuilding…" : "Rebuild all"}
                </span>
              </Button>
            </span>
            <Button size="xs" onClick={onAdd}>
              <span className="flex items-center gap-1">
                <PlusIcon className="h-3.5 w-3.5" /> Add
              </span>
            </Button>
          </div>
        </div>

        <div className="relative">
          <TextField
            fullWidth
            small
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.currentTarget.value)}
            className="!py-1.5 pr-8"
          />
          <MagnifyingGlassIcon className="pointer-events-none absolute right-2 top-2 h-4 w-4 text-gray-400" />
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
        {isLoading && (
          <p className="px-3 py-4 text-sm text-gray-500">Loading categories…</p>
        )}

        {/* An empty list and a failed request look identical to the user unless we say so, and
            claiming "none configured" when we never got an answer sends people the wrong way. */}
        {!isLoading && error && (
          <div className="m-3 rounded border border-red-200 bg-red-50 p-3">
            <p className="text-sm font-medium text-red-800">
              Could not load categories
            </p>
            <p className="mt-1 text-xs text-red-700">{error.message}</p>
          </div>
        )}

        {!isLoading && !error && categories.length === 0 && (
          <div className="px-3 py-6 text-center">
            <p className="text-sm text-gray-600">
              {isSearching
                ? "No configured categories match that search."
                : "No categories configured yet."}
            </p>
            {!isSearching && (
              <>
                <p className="mt-1 text-xs text-gray-500">
                  Every category uses the built-in title rules until you add one
                  here.
                </p>
                <Button size="sm" className="mt-3" onClick={onAdd}>
                  Add a category
                </Button>
              </>
            )}
          </div>
        )}

        <ul className="divide-y divide-gray-100">
          {categories.map((category) => (
            <li
              key={category.partCategoryId}
              className={clsx(
                "group flex items-center",
                selectedId === category.partCategoryId
                  ? "bg-orange-50"
                  : "hover:bg-gray-50",
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(category.partCategoryId)}
                className={clsx(
                  "flex flex-1 items-center justify-between gap-2 px-3 py-2 text-left text-sm min-w-0",
                  selectedId === category.partCategoryId && "font-medium",
                )}
              >
                <span className="truncate">{category.name}</span>

                <span className="flex shrink-0 items-center gap-1">
                  {!category.isActive && (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
                      Inactive
                    </span>
                  )}
                  <span
                    className={clsx(
                      "rounded px-1.5 py-0.5 text-[11px]",
                      category.titleType === "Mechanical"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-green-100 text-green-700",
                    )}
                  >
                    {category.titleType}
                  </span>
                </span>
              </button>

              <button
                type="button"
                aria-label={`Remove ${category.name}`}
                title={`Remove ${category.name}`}
                onClick={() => onRemove(category)}
                className="px-2 text-gray-300 opacity-0 transition-opacity hover:text-red-600 focus:opacity-100 group-hover:opacity-100"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
        Categories not listed here use the built-in title rules.
      </p>
    </div>
  );
}
